import { useEffect, useState } from 'react';
import { Validator, useErrorHandling } from '@/utils/errorHandling';
import { CompanyAccountsTable } from './CompanyAccountsTable';
import { Box, CircularProgress } from '@mui/material';
import { PrimaryButton, DangerButton } from '@/components/shared/Button';
import { useTranslation } from 'react-i18next';
import {
    AddCompanyAccountTransactionDialog,
    AddCompanyAccountTransactionDialogMode
} from './AddCompanyAccountTransactionDialog';
import { CreateCompanyAccountTransactionRequest } from '@/models/payment';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { useGetCompanyAccounts } from '@/services/paymentsQuery';
import { usePaymentsMutations } from '@/services/paymentsMutations';

export const CompanyAccountsOverview = () => {
    const { backendErrorHandler } = useErrorHandling();
    const { setAlert } = useNotificationsSnackbar();
    const { t } = useTranslation();

    const [mode, setMode] =
        useState<AddCompanyAccountTransactionDialogMode>('Deposit');
    const [open, setOpen] = useState(false);
    const [validator, setValidator] = useState(new Validator());

    const {
        data: companyAccountsResponse,
        isLoading,
        isError: isFetchError,
        error: fetchError
    } = useGetCompanyAccounts();
    const companyAccounts = companyAccountsResponse?.data;

    useEffect(() => {
        if (isFetchError) {
            backendErrorHandler('Error fetching company accounts: ')(
                fetchError as Error
            );
        }
    }, [isFetchError]);

    const { createCompanyAccountTransaction } = usePaymentsMutations();

    // The success/error split lives here (not in .then/.catch) so the
    // "added successfully" snackbar only fires on an actual 2xx — a 400
    // now routes to the error handler and surfaces the backend validation.
    useEffect(() => {
        if (createCompanyAccountTransaction.isSuccess) {
            setAlert('New transaction added successfully.', 'success');
            setValidator(new Validator());
            setOpen(false);
        }
    }, [createCompanyAccountTransaction.isSuccess]);

    useEffect(() => {
        if (createCompanyAccountTransaction.isError) {
            backendErrorHandler(undefined, (_e, errorDetails) => {
                setValidator(new Validator(errorDetails?.errors));
            })(createCompanyAccountTransaction.error);
        }
    }, [createCompanyAccountTransaction.isError]);

    const onDepositClick = () => {
        setMode('Deposit');
        setValidator(new Validator());
        setOpen(true);
    };

    const onWithdrawClick = () => {
        setMode('Withdraw');
        setValidator(new Validator());
        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
    };

    const onSubmit = (request: CreateCompanyAccountTransactionRequest) => {
        createCompanyAccountTransaction.mutate(request);
    };

    return (
        <>
            <Box sx={{ marginTop: '1rem' }}>
                <PrimaryButton
                    style={{ marginRight: '1rem' }}
                    onClick={onDepositClick}
                >
                    {t('company_accounts.deposit')}
                </PrimaryButton>
                <DangerButton onClick={onWithdrawClick}>
                    {t('company_accounts.withdraw')}
                </DangerButton>
            </Box>

            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                companyAccounts && (
                    <>
                        <AddCompanyAccountTransactionDialog
                            companyAccounts={companyAccounts}
                            mode={mode}
                            validator={validator}
                            setValidator={setValidator}
                            open={open}
                            onClose={onClose}
                            onSubmit={onSubmit}
                            isSubmitting={
                                createCompanyAccountTransaction.isPending
                            }
                        />

                        <CompanyAccountsTable
                            companyAccounts={companyAccounts}
                        />
                    </>
                )
            )}
        </>
    );
};
